import {
  Labeled,
  LabeledProps,
  Theme,
  Tooltip,
} from '@infotech-soe/erp-ui-kit';
import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';

import {
  ColorInputFormControlViewModel,
  DatasetFormControlViewModel,
  DateFormControlViewModel,
  DictDateFormControlViewModel,
  DictionaryInputFormControlViewModel,
  DictionaryTreeFormControlViewModel,
  DictMultilineFormControlViewModel,
  FileInputFormControlViewModel,
  FormControlViewModelType,
  MonthYearFormControlViewModel,
  MultilineFormControlViewModel,
  NumericInputFormControlViewModel,
  RichTextFormControlViewModel,
  SelectFormControlViewModel,
  TextInputFormControlViewModel,
  TimeInputFormControlViewModel,
} from '../../../services/form/view-model/FormControlViewModel';

import { useFormContext } from './FormContext';
import { TFormControlConnectorProps } from './FormControlContainer';

interface Props
  extends
    Pick<TFormControlConnectorProps, 'areHintsShown'>,
    Pick<LabeledProps, 'onClick'> {
  control:
    | DateFormControlViewModel
    | DictDateFormControlViewModel
    | DictionaryInputFormControlViewModel
    | FileInputFormControlViewModel
    | NumericInputFormControlViewModel
    | SelectFormControlViewModel
    | TextInputFormControlViewModel
    | ColorInputFormControlViewModel
    | MultilineFormControlViewModel
    | MonthYearFormControlViewModel
    | RichTextFormControlViewModel
    | DatasetFormControlViewModel
    | DictionaryTreeFormControlViewModel
    | TimeInputFormControlViewModel
    | DictMultilineFormControlViewModel;
  children: any;
}

const useStyles = createUseStyles((theme: Theme) => ({
  tooltipTarget: {
    display: 'block',
    width: '100%',
    height: '100%',
  },
  tooltipError: {
    color: theme.colors.warning,
  },
}));

const FormLabeledInputControl: React.FC<Props> = ({
  areHintsShown,
  control,
  children,
  onClick,
}) => {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const { errorMessages } = useFormContext();

  const validationErrorReason = errorMessages.get(control.name);
  const errorMessage = control.errorMessage || validationErrorReason;
  const hasInvalidReason =
    !!errorMessage && (control.invalid || !!validationErrorReason);

  let childColumns = 6;
  if (control.noLabel || control.labelWidth === 0) {
    childColumns = 12;
  } else if (control.labelWidth) {
    if (typeof control.labelWidth === 'string') {
      if (control.labelWidth.endsWith('%')) {
        const labelWidth = 100 - Number.parseFloat(control.labelWidth);
        childColumns = Number.isNaN(labelWidth)
          ? 6
          : Math.floor((12 * labelWidth) / 100);
      } else {
        const labelWidth = Number.parseInt(control.labelWidth, 10);
        childColumns = Number.isNaN(labelWidth) ? 6 : 12 - labelWidth;
      }
    } else if (typeof control.labelWidth === 'number') {
      childColumns = 12 - control.labelWidth;
    }
  }
  const pos =
    control.type === FormControlViewModelType.MULTILINE ||
    control.type === FormControlViewModelType.RICHTEXT ||
    control.type === FormControlViewModelType.DATASET
      ? 'top'
      : theme.labelPosition;
  return (
    <Labeled
      {...(areHintsShown && { hint: control.hint })}
      disabled={control.disabled}
      // this hack is needed to fix top labeled position when label is empty
      label={
        (control.noLabel ? null : control.label) ??
        (pos === 'top' ? '\xa0' : '')
      }
      labelPosition={pos as LabeledProps['labelPosition']}
      labelVerticalAlign="center"
      childColumns={childColumns as LabeledProps['childColumns']}
      required={control.required}
      onClick={onClick}
    >
      <Tooltip
        label={hasInvalidReason ? errorMessage : undefined}
        placement="bottom"
        className={classes.tooltipError}
      >
        <span className={classes.tooltipTarget}>{children}</span>
      </Tooltip>
    </Labeled>
  );
};

export default FormLabeledInputControl;
